import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { CheckSquare, FolderInput, Trash2, UserPlus, X } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { useEmployeeNameFormatter } from '@/lib/employee-name';
import { useDebouncedValue } from '@/hooks/use-debounce';
import { useBoards, useAnyBoardPermission } from '@/hooks/use-boards';
import { useAllIssues, useCrossBoardBulkAction } from '@/hooks/use-all-issues';
import type {
  BoardListItem,
  ProjectMemberItem,
  TaskAssignee,
  TaskStatusItem,
} from '@hygge/shared';
import { ListView } from './list-view';
import {
  TaskFiltersBar,
  emptyFilters,
  activeFilterCount,
  hasActiveClientFilters,
  taskMatchesFilters,
  type FilterState,
} from './task-filters';
import { QuickFilters } from './quick-filters';

interface AllIssuesViewProps {
  onTaskSelect: (key: string) => void;
}

/**
 * Cross-board "All issues" screen. Reuses the board-agnostic `ListView` (with a
 * leading Board column) over the pooled `/all-issues` query, driven by the same
 * client-side filters as the per-board views. Filter option sets (status /
 * assignee / label) are derived from the loaded pool — cross-board there is no
 * single board's status/member list to read from.
 */
export function AllIssuesView({ onTaskSelect }: AllIssuesViewProps) {
  // All Issues spans boards, so no single board answers this. The control is
  // offered when at least one board the caller can see allows it, and the
  // per-board check still refuses the selection that reaches across to a board
  // that does not. Gating on one board would hide it wrongly; gating on none
  // would offer it to somebody who may bulk-edit nowhere.
  const canBulk = useAnyBoardPermission('task.bulk');
  const [filters, setFilters] = useState<FilterState>(emptyFilters);
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  const debouncedSearch = useDebouncedValue(filters.search, 300);
  const { data, isLoading } = useAllIssues(
    debouncedSearch ? { search: debouncedSearch } : undefined,
  );
  const tasks = useMemo(() => data?.data ?? [], [data]);
  // The server's real total — the loaded set is capped (client-side filters run
  // over it), so `total` can exceed what's on screen. Surface that honestly.
  const total = data?.meta.total ?? tasks.length;
  const isTruncated = total > tasks.length;

  // ─── Filter option sets derived from the loaded pool ────────────────────
  const statuses = useMemo(() => {
    const m = new Map<string, TaskStatusItem>();
    for (const t of tasks) if (t.status) m.set(t.status.id, t.status);
    return [...m.values()];
  }, [tasks]);

  const members = useMemo(() => {
    const m = new Map<string, ProjectMemberItem>();
    for (const t of tasks) {
      const people: TaskAssignee[] = t.assignees ?? (t.assignee ? [t.assignee] : []);
      for (const p of people) {
        if (m.has(p.id)) continue;
        m.set(p.id, {
          id: p.id,
          employeeId: p.id,
          firstName: p.firstName,
          lastName: p.lastName,
          avatarUrl: p.avatarUrl,
          departmentName: null,
          location: null,
          role: null,
          isManager: false,
          joinedAt: null,
          removedAt: null,
        });
      }
    }
    return [...m.values()];
  }, [tasks]);

  const labelOptions = useMemo(() => {
    const s = new Set<string>();
    for (const t of tasks) {
      for (const l of t.labels ?? []) s.add(l);
      for (const tag of t.tags ?? []) s.add(tag.name);
    }
    return [...s];
  }, [tasks]);

  // ─── Client-side dimension filtering over the loaded pool ───────────────
  const todayIso = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const visibleTasks = useMemo(
    () => tasks.filter((t) => taskMatchesFilters(t, filters, todayIso)),
    [tasks, filters, todayIso],
  );

  // taskKey → owning board id, for splitting a cross-board bulk selection.
  const keyToBoardId = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tasks) m.set(t.key, t.board.id);
    return m;
  }, [tasks]);

  const toggleSelect = useCallback((key: string) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const clearSelection = useCallback(() => setSelectedKeys(new Set()), []);
  const toggleSelectionMode = useCallback(() => {
    setSelectionMode((prev) => {
      if (prev) setSelectedKeys(new Set());
      return !prev;
    });
  }, []);

  // Keep the selection confined to what's on screen — drop keys a filter hides.
  useEffect(() => {
    if (!selectionMode) return;
    setSelectedKeys((prev) => {
      if (prev.size === 0) return prev;
      const visibleKeys = new Set(visibleTasks.map((t) => t.key));
      let changed = false;
      const next = new Set<string>();
      for (const key of prev) {
        if (visibleKeys.has(key)) next.add(key);
        else changed = true;
      }
      return changed ? next : prev;
    });
  }, [visibleTasks, selectionMode]);

  const activeCount = activeFilterCount(filters);
  const clientActive = hasActiveClientFilters(filters);
  const showFilters = clientActive || filters.search !== '' || activeCount > 0;

  return (
    <>
      {/* Toolbar */}
      <TooltipProvider delayDuration={0}>
        <div className="flex items-center justify-between px-6 py-3 border-b border-border bg-background">
          <div className="flex items-center gap-3">
            <h2 className="text-sm font-semibold text-foreground">All issues</h2>
            <Badge variant="outline" className="text-xs px-2 py-0.5 gap-1.5 font-normal">
              {total} task{total !== 1 ? 's' : ''}
            </Badge>
            <QuickFilters filters={filters} onChange={setFilters} />
          </div>
          {canBulk && (
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant="outline"
                  size="icon"
                  className={cn(
                    'h-9 w-9 shrink-0 rounded-full text-muted-foreground hover:text-foreground cursor-pointer',
                    selectionMode &&
                      'border-hygge/30 bg-hygge/10 text-hygge hover:bg-hygge/20 hover:text-hygge',
                  )}
                  onClick={toggleSelectionMode}
                  aria-label="Select tasks"
                  aria-pressed={selectionMode}
                >
                  <CheckSquare className="h-4 w-4" />
                </Button>
              </TooltipTrigger>
              <TooltipContent side="bottom">
                <p>{selectionMode ? 'Exit select mode' : 'Select tasks'}</p>
              </TooltipContent>
            </Tooltip>
          )}
        </div>
      </TooltipProvider>

      {/* Truncation notice — the loaded pool is capped; be honest about it. */}
      {isTruncated && (
        <div className="border-b border-border bg-muted/10 px-6 py-1.5 text-xs text-muted-foreground">
          Showing the first {tasks.length} of {total} issues — refine filters to narrow the results.
        </div>
      )}

      {/* Filter bar */}
      {showFilters && (
        <TaskFiltersBar
          filters={filters}
          onChange={setFilters}
          statuses={statuses}
          members={members}
          labelOptions={labelOptions}
          shownCount={visibleTasks.length}
          totalCount={tasks.length}
          truncated={isTruncated}
        />
      )}

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full rounded-md" />
            {Array.from({ length: 8 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full rounded-md" />
            ))}
          </div>
        ) : (
          <ListView
            tasks={visibleTasks}
            statuses={statuses}
            onTaskSelect={onTaskSelect}
            selectionMode={selectionMode}
            selectedKeys={selectedKeys}
            onToggleSelect={toggleSelect}
            showBoard
          />
        )}
      </div>

      {/* Cross-board bulk bar */}
      {canBulk && selectionMode && (
        <AllIssuesBulkBar
          selectedKeys={selectedKeys}
          keyToBoardId={keyToBoardId}
          members={members}
          onDone={clearSelection}
          onSucceeded={() => setSelectionMode(false)}
        />
      )}
    </>
  );
}

// ─── Cross-board bulk bar ──────────────────────────────────────────────────
// Only offers actions that are meaningful across boards: assign (employee-scoped),
// move-to-board (target is global), delete. "Move to status" is intentionally
// omitted — statuses are board-specific and don't translate across boards.
interface AllIssuesBulkBarProps {
  selectedKeys: Set<string>;
  keyToBoardId: Map<string, string>;
  members: ProjectMemberItem[];
  onDone: () => void;
  /** Called after an action succeeded — All Issues leaves select mode then. */
  onSucceeded?: () => void;
}

function AllIssuesBulkBar({ selectedKeys, keyToBoardId, members, onDone, onSucceeded }: AllIssuesBulkBarProps) {
  const formatName = useEmployeeNameFormatter();
  const bulk = useCrossBoardBulkAction();
  const { data: boards } = useBoards();
  // Every action here goes to the bulk route of the board each task is on, which
  // asks for `task.bulk`; delete additionally needs a delete permission there.
  // So each is offered when a board the selection actually touches allows it —
  // the bar itself opens when any board allows `task.bulk`, a different question.
  const permsOf = new Map((boards ?? []).map((b) => [b.id, b.currentUserPermissions ?? []]));
  const someSelectedBoard = (allows: (perms: string[]) => boolean) =>
    [...selectedKeys].some((key) => {
      const boardId = keyToBoardId.get(key);
      return boardId !== undefined && allows(permsOf.get(boardId) ?? []);
    });
  const canBulkSelection = someSelectedBoard((p) => p.includes('task.bulk'));
  const canDelete = someSelectedBoard(
    (p) => p.includes('task.bulk') && (p.includes('task.delete.own') || p.includes('task.delete.any')),
  );
  const [pendingBoard, setPendingBoard] = useState<BoardListItem | null>(null);
  const count = selectedKeys.size;

  if (count === 0) return null;

  const run = (payload: Parameters<typeof bulk.mutate>[0]['payload'], verb = 'updated') => {
    bulk.mutate(
      { keys: [...selectedKeys], keyToBoardId, payload },
      {
        onSuccess: (res) => {
          toast.success(
            res.failed.length
              ? `${res.affected} tasks ${verb}, ${res.failed.length} failed`
              : `${res.affected} tasks ${verb}`,
          );
          onDone();
          // Only when something was done; after an all-refused action select mode stays on.
          if (res.affected > 0) onSucceeded?.();
        },
        onError: () => toast.error('Bulk action failed'),
      },
    );
  };

  return (
    <div className="fixed bottom-6 left-1/2 z-50 flex -translate-x-1/2 items-center gap-1.5 rounded-full border border-border bg-background/95 px-3 py-2 shadow-lg backdrop-blur">
      <span className="px-2 text-sm font-medium tabular-nums">{count} selected</span>
      <div className="mx-0.5 h-5 w-px bg-border" aria-hidden />

      {canBulkSelection && (
      <>
      {/* Assign */}
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 rounded-full cursor-pointer"
            disabled={bulk.isPending}
          >
            <UserPlus className="h-4 w-4" />
            Assign
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="center" className="w-60">
          <DropdownMenuLabel className="flex flex-col gap-0.5">
            Assign to
            <span className="text-[11px] font-normal text-muted-foreground">
              Adds to the current assignees
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            className="cursor-pointer"
            onSelect={() => run({ action: 'assign', assigneeId: null })}
          >
            <span className="mr-2 h-5 w-5 shrink-0 rounded-full border border-dashed border-border" />
            Unassigned
          </DropdownMenuItem>
          <div className="max-h-64 overflow-y-auto">
            {members.map((m) => (
              <DropdownMenuItem
                key={m.employeeId}
                className="cursor-pointer"
                onSelect={() => run({ action: 'assign', assigneeId: m.employeeId })}
              >
                <Avatar className="mr-2 h-5 w-5">
                  <AvatarImage src={m.avatarUrl || undefined} />
                  <AvatarFallback className="text-[8px]">
                    {m.firstName[0]}
                    {m.lastName[0]}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate">
                  {formatName({ firstName: m.firstName, lastName: m.lastName })}
                </span>
              </DropdownMenuItem>
            ))}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Move to board */}
      {(boards?.length ?? 0) > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="h-8 gap-1.5 rounded-full cursor-pointer"
              disabled={bulk.isPending}
            >
              <FolderInput className="h-4 w-4" />
              Move to board
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="center" className="w-56">
            <DropdownMenuLabel>Move to board</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <div className="max-h-64 overflow-y-auto">
              {(boards ?? []).map((b) => (
                <DropdownMenuItem
                  key={b.id}
                  className="cursor-pointer"
                  onSelect={() => setPendingBoard(b)}
                >
                  <span
                    className="mr-2 h-2.5 w-2.5 shrink-0 rounded-full"
                    style={{ backgroundColor: b.color }}
                  />
                  <span className="truncate">{b.name}</span>
                </DropdownMenuItem>
              ))}
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Move-to-board confirm */}
      <AlertDialog
        open={pendingBoard !== null}
        onOpenChange={(open) => {
          if (!open) setPendingBoard(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              Move {count} task{count !== 1 ? 's' : ''} to {pendingBoard?.name}?
            </AlertDialogTitle>
            <AlertDialogDescription>
              Each task gets a new key on {pendingBoard?.name}. Its sprint, tags and parent link are
              dropped (all board-specific); assignees, labels, comments and activity history are
              kept. Tasks already on {pendingBoard?.name} are left untouched.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="cursor-pointer"
              onClick={() => {
                if (pendingBoard) {
                  run({ action: 'move_board', targetBoardId: pendingBoard.id }, 'moved');
                }
                setPendingBoard(null);
              }}
            >
              Move
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      </>
      )}

      {/* Delete */}
      {canDelete && (
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button
            variant="ghost"
            size="sm"
            className="h-8 gap-1.5 rounded-full text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-950/40 cursor-pointer"
            disabled={bulk.isPending}
          >
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {count} task{count !== 1 ? 's' : ''}?</AlertDialogTitle>
            <AlertDialogDescription>
              This soft-deletes the selected task{count !== 1 ? 's' : ''}. They are removed from
              their boards but their links and activity history are preserved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="cursor-pointer">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 cursor-pointer"
              onClick={() => run({ action: 'delete' }, 'deleted')}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      )}

      <div className="mx-0.5 h-5 w-px bg-border" aria-hidden />
      <Button
        variant="ghost"
        size="sm"
        className="h-8 gap-1.5 rounded-full text-muted-foreground hover:text-foreground cursor-pointer"
        onClick={onDone}
      >
        <X className="h-4 w-4" />
        Clear
      </Button>
    </div>
  );
}
